import { create } from "zustand";
import { passesApi, vipApi } from "../store/api.js";
import { luckyHubApi, type LuckyHubSummary } from "../store/luckyHubApi.js";
import type { PassView } from "../store/types.js";
import { useSession } from "./session.js";

/**
 * What waits to be collected outside the events feed (polish round, 24.09.): pass tiers, the VIP chest and the lucky
 * games. Each used to be known only once its own page had been opened, so the bar, the event tiles and the store tabs
 * had nothing to point at — a player with six pass rewards and a VIP chest saw no mark anywhere until they went looking.
 *
 * Loaded when the session starts, again when the player moves between places (at most every 20 s) and right after a
 * page collects something (`refreshAttention(true)`).
 */
export interface AttentionState {
  /** Claimable tiers per pass id (the season and the weekly pass count for the events; every pass for the store). */
  passes: Record<string, number>;
  /** The pass ids of kind `battle`: the ones Events › Pass shows. */
  battlePasses: string[];
  vipChest: boolean;
  lucky: LuckyHubSummary | null;
  loadedAt: number;
  owner: string | null;
}

export const useAttention = create<AttentionState>(() => ({ passes: {}, battlePasses: [], vipChest: false, lucky: null, loadedAt: 0, owner: null }));

let inFlight: Promise<void> | null = null;

export function refreshAttention(force = false): Promise<void> {
  const owner = useSession.getState().account?.id ?? null;
  if (!owner) return Promise.resolve();
  const state = useAttention.getState();
  if (!force && state.owner === owner && Date.now() - state.loadedAt < 20_000) return Promise.resolve();
  if (inFlight && !force) return inFlight;
  const run = (async () => {
    const [passes, vip, lucky] = await Promise.allSettled([passesApi.list(), vipApi.view(), luckyHubApi.hub()]);
    if (useSession.getState().account?.id !== owner) return;
    const previous = useAttention.getState().owner === owner ? useAttention.getState() : null;
    const list: PassView[] | null = passes.status === "fulfilled" ? passes.value : null;
    useAttention.setState({
      owner,
      loadedAt: Date.now(),
      passes: list ? Object.fromEntries(list.map((p) => [p.id, p.claimable.length])) : previous?.passes ?? {},
      battlePasses: list ? list.filter((p) => p.kind === "battle").map((p) => p.id) : previous?.battlePasses ?? [],
      vipChest: vip.status === "fulfilled" ? vip.value.chest.claimable : previous?.vipChest ?? false,
      lucky: lucky.status === "fulfilled" ? lucky.value : previous?.lucky ?? null,
    });
  })().finally(() => { if (inFlight === run) inFlight = null; });
  inFlight = run;
  return run;
}

/** Pass tiers waiting in Events › Pass (the season and the weekly pass). */
export function passBadge(s: AttentionState): number {
  return s.battlePasses.reduce((n, id) => n + (s.passes[id] ?? 0), 0);
}

/** Every pass tier waiting, for the store's pass page. */
export function allPassesBadge(s: AttentionState): number {
  return Object.values(s.passes).reduce((n, v) => n + v, 0);
}

/** Free plays and finished lucky missions: the Luck hub's own "3 games free today", as a number the tiles can show. */
export function luckyBadge(s: AttentionState): number {
  const l = s.lucky;
  if (!l) return 0;
  return l.claimable + (l.wheel.freeLeft > 0 ? 1 : 0) + (l.capsule.freeLeft > 0 ? 1 : 0) + (l.memory.free ? 1 : 0);
}
